import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { profileAvatar } from "../component/ImageList";
import baseURL from "../component/baseURL";
import { useNavigate, useLocation } from 'react-router-dom';
import stringsoflanguages from '../component/stringsoflanguages';

/** Legacy session key — now stores grid `scrollTop` (pixels inside `.member-cards-grid`). */
const GLOBAL_MEMBERS_SCROLL_KEY = 'globalMembersScrollPosition';
const GLOBAL_MEMBERS_GRID_SCROLL_KEY = 'yesmls_globalMembers_gridScrollTop';
const GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY = 'yesmls_globalMembers_restoreScrollPending';

function readRestoreScrollPending() {
    try {
        return typeof sessionStorage !== 'undefined' && sessionStorage.getItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY) === '1';
    } catch {
        return false;
    }
}

export default function GlobalMembers({ isMobile, onOpenMemberProfile }) {
    const [userID, setUserID] = useState(null);
    const [globalMembers, setGlobalMembers] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [isPremium, setIsPremium] = useState(false);
    const [loading, setLoading] = useState(true);
    const [gearMenuOpen, setGearMenuOpen] = useState(false);
    const [gearSpinning, setGearSpinning] = useState(false);
    const gearMenuRef = useRef(null);
    /** `.member-cards-grid` has overflow-y: auto — list scroll is here, not `window` */
    const listScrollRef = useRef(null);
    /** Hide grid until scroll is applied after back from profile — removes visible jerk */
    const [gridHiddenForScrollRestore, setGridHiddenForScrollRestore] = useState(readRestoreScrollPending);
    const navigate = useNavigate();
    const location = useLocation();
    const [filterStatus, setFilterStatus] = useState(0);
    const searchLower = (searchQuery || '').trim().toLowerCase();
    const filteredMembers = searchLower
        ? globalMembers.filter((m) => {
            const fname = (m.member_fname ?? '').toLowerCase();
            const lname = (m.member_lname ?? '').toLowerCase();
            const title = (m.title ?? '').toLowerCase();
            const city = (m.city_name ?? '').toLowerCase();
            const country = (m.country_name ?? '').toLowerCase();
            const fullName = `${fname} ${lname}`.trim();
            return fullName.includes(searchLower) || fname.includes(searchLower) || lname.includes(searchLower)
                || title.includes(searchLower) || city.includes(searchLower) || country.includes(searchLower);
        })
        : globalMembers;

    const handleMemberCardClick = (member) => {
        const el = listScrollRef.current;
        const gridScroll = el ? el.scrollTop : 0;
        sessionStorage.setItem(GLOBAL_MEMBERS_GRID_SCROLL_KEY, String(gridScroll));
        sessionStorage.setItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY, '1');
        sessionStorage.setItem(GLOBAL_MEMBERS_SCROLL_KEY, String(gridScroll));
        const listState = { scrollPosition: gridScroll, fromGlobalMembersRoot: true };
        if (location.state?.returnTo) {
            listState.returnTo = location.state.returnTo;
        }

        navigate('/member-profile', {
            state: {
                memberID: member.user_id,
                member,
                scrollPosition: gridScroll,
                from: '/home/global-members',
                returnTo: {
                    path: '/home/global-members',
                    state: listState,
                },
            },
        });
        onOpenMemberProfile?.(member.user_id);
    };

    /** Persist grid scroll while browsing */
    useEffect(() => {
        if (loading) return undefined;
        const el = listScrollRef.current;
        if (!el) return undefined;
        let timeoutId;
        const onScroll = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                sessionStorage.setItem(GLOBAL_MEMBERS_GRID_SCROLL_KEY, String(el.scrollTop));
            }, 120);
        };
        el.addEventListener('scroll', onScroll, { passive: true });
        return () => {
            clearTimeout(timeoutId);
            el.removeEventListener('scroll', onScroll);
        };
    }, [loading]);

    /** One-shot scroll restore before paint + fade-in (avoids triple `scrollTop` jitter). */
    useLayoutEffect(() => {
        if (loading) return undefined;
        if (sessionStorage.getItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY) !== '1') {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const raw =
            sessionStorage.getItem(GLOBAL_MEMBERS_GRID_SCROLL_KEY) ??
            sessionStorage.getItem(GLOBAL_MEMBERS_SCROLL_KEY);
        if (raw == null || raw === '') {
            sessionStorage.removeItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }
        const y = Number(raw);
        if (!Number.isFinite(y) || y < 0) {
            sessionStorage.removeItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const el = listScrollRef.current;
        if (!el) {
            sessionStorage.removeItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY);
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        const reducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
        el.scrollTop = y;
        sessionStorage.removeItem(GLOBAL_MEMBERS_RESTORE_SCROLL_PENDING_KEY);

        if (reducedMotion) {
            setGridHiddenForScrollRestore(false);
            return undefined;
        }

        requestAnimationFrame(() => {
            setGridHiddenForScrollRestore(false);
        });
        return undefined;
    }, [loading, filteredMembers.length]);

    useEffect(() => {
        const userid = localStorage.getItem('user_id');
        setUserID(userid);
        fetchGlobalMembersAPI(userid);
    }, []);

    useEffect(() => {
        const handleGearClickOutside = (e) => {
            if (gearMenuRef.current && !gearMenuRef.current.contains(e.target)) {
                setGearMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleGearClickOutside);
        return () => document.removeEventListener('mousedown', handleGearClickOutside);
    }, []);

    function fetchGlobalMembersAPI(userid) {
        // console.log(this.state.userID)
        // const { navigate } = this.props.navigation;
        let formdata = new FormData();
        formdata.append("user_id", userid)
        formdata.append("key", "81dc9bdb52d04dc20036dbd8313ed055")
        formdata.append("member_type", 'pool')
        console.log(formdata);
        fetch(baseURL.URL + '/member/fetch_members', {
            method: 'POST',
            body: formdata
        }).then((response) => response.json())
            .then((responseJson) => {
                console.log(responseJson);
                const result = responseJson.response.member_list
                console.log(result[1]);
                if (responseJson.server.result_code === 200) {
                    const res = responseJson.response;
                    setGlobalMembers(result || []);
                    const premium = res.isPremium === true || res.is_premium === true || res.is_premium === 1;
                    setIsPremium(Boolean(premium));
                    setFilterStatus(responseJson.response.filter_on);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
            });
    }

    if (loading) {
        return (
            <div className="content-right global-members-page page-loader-wrapper">
                <div className="react-js-loader">
                    <div className="react-js-loader-spinner"></div>
                    <p className="react-js-loader-text">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="content-right global-members-page">
            {/* Search and Filter Section */}
            <div className="search-filter-section">

                <div className="search-bar">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        viewBox="0 0 20 20"
                        fill="none"
                        className="search-icon"
                    >
                        <path
                            d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z"
                            stroke="#E1730B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                        <path
                            d="M17.5 17.5L13.875 13.875"
                            stroke="#E1730B"
                            strokeWidth="1.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                    <input
                        type="text"
                        placeholder="Search by name / company"
                        className="search-input"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                    />
                </div>
                <button className="filter-btn">
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="25"
                        height="25"
                        viewBox="0 0 25 25"
                        fill="none"
                    >
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M16.4062 4.69678C14.5256 4.69678 12.9441 6.04475 12.5793 7.82178H2.34375C1.91228 7.82178 1.5625 8.17156 1.5625 8.60303C1.5625 9.0345 1.91228 9.38428 2.34375 9.38428H12.5793C12.9441 11.1613 14.5256 12.5093 16.4062 12.5093C18.2869 12.5093 19.8684 11.1613 20.2332 9.38428H22.6562C23.0877 9.38428 23.4375 9.0345 23.4375 8.60303C23.4375 8.17156 23.0877 7.82178 22.6562 7.82178H20.2332C19.8684 6.04475 18.2869 4.69678 16.4062 4.69678ZM16.4062 6.25928C17.7099 6.25928 18.75 7.29938 18.75 8.60303C18.75 9.90668 17.7099 10.9468 16.4062 10.9468C15.1026 10.9468 14.0625 9.90668 14.0625 8.60303C14.0625 7.29938 15.1026 6.25928 16.4062 6.25928Z"
                            fill="white"
                        />
                        <path
                            fill-rule="evenodd"
                            clip-rule="evenodd"
                            d="M8.59375 12.5093C6.71313 12.5093 5.1316 13.8573 4.76685 15.6343H2.34375C1.91228 15.6343 1.5625 15.9841 1.5625 16.4155C1.5625 16.847 1.91228 17.1968 2.34375 17.1968H4.76685C5.1316 18.9738 6.71313 20.3218 8.59375 20.3218C10.4744 20.3218 12.0559 18.9738 12.4207 17.1968H22.6562C23.0877 17.1968 23.4375 16.847 23.4375 16.4155C23.4375 15.9841 23.0877 15.6343 22.6562 15.6343H12.4207C12.0559 13.8573 10.4744 12.5093 8.59375 12.5093ZM8.59375 14.0718C9.8974 14.0718 10.9375 15.1119 10.9375 16.4155C10.9375 17.7192 9.8974 18.7593 8.59375 18.7593C7.2901 18.7593 6.25 17.7192 6.25 16.4155C6.25 15.1119 7.2901 14.0718 8.59375 14.0718Z"
                            fill="white"
                        />
                    </svg>
                </button>
            </div>
            <span className='listing-count'>{stringsoflanguages.noofmembers}: {filteredMembers.length}</span>
            {/* Member Cards Grid */}
            <div
                className={`member-cards-grid${gridHiddenForScrollRestore ? ' gm-grid--restore-hidden' : ''}`}
                ref={listScrollRef}
            >
                {filteredMembers.length === 0 ? (
                    <p className="no-data-message">{searchQuery.trim() ? 'No members match your search.' : 'No members found.'}</p>
                ) : (
                    filteredMembers.map((member) => (
                        <div className="member-card cursor-pointer" key={member.id} onClick={() => handleMemberCardClick(member)}>
                            <div className="member-info">
                                <div className='flex align-items-center gap-2'>
                                    <p className="member-since">{stringsoflanguages.memberSince}: {member.added_date}</p>
                                    {member.check_conn == 1 ? (
                                        <p className="connection-status">{stringsoflanguages.connected}</p>
                                    ) : null}
                                </div>
                                <h3 className="member-name">{member.member_fname} {member.member_lname}</h3>
                                <p className="member-role">{member.title}</p>
                                <p className="member-location">{member.city_name}, {member.country_name}</p>



                            </div>
                            <div className="member-image">
                                <img src={member.member_image_thumb} alt={member.member_fname} />
                            </div>
                        </div>
                    ))
                )}
            </div>
            {/* Desktop: gear icon with dropdown (Lock, Filter) */}
            {!isMobile && (
                <div className="btn-section btn-section-gear" ref={gearMenuRef}>
                    <button
                        type="button"
                        className={`btn-primary gear-btn ${gearSpinning ? 'gear-spin' : ''}`}
                        onClick={() => {
                            setGearMenuOpen((prev) => !prev);
                            setGearSpinning(true);
                            setTimeout(() => setGearSpinning(false), 500);
                        }}
                        aria-label="Options"
                        aria-expanded={gearMenuOpen}
                    >
                        <i className="fa-solid fa-gear"></i>
                    </button>
                    {gearMenuOpen && (
                        <ul className="gear-dropdown" aria-label="Options list">
                            <li>
                                <button type="button" className="gear-dropdown-item" onClick={() => { setGearMenuOpen(false); navigate('/home/blocked-members'); }}>
                                    <i className="fa-solid fa-lock"></i>
                                    <span>{stringsoflanguages.blockedMembers}</span>
                                </button>
                            </li>
                            <li>
                                <button type="button" className="gear-dropdown-item relative" onClick={() => { setGearMenuOpen(false); navigate('/home/member-filter'); }}>
                                    {filterStatus === 1 ? <span className="filter-active-icon"><i className="fa-solid fa-check"></i></span> : null}
                                    <i className="fa-solid fa-sliders"></i>
                                    <span>{stringsoflanguages.filter}</span>
                                </button>
                            </li>
                        </ul>
                    )}
                </div>
            )}

            {/* Mobile: bottom nav with Lock and Filter */}
            {isMobile && (
                <>
                    <nav className="mobile-bottom-nav liquidGlass-wrapper dock global-members-footer-nav" aria-label="Global members actions">
                        <div className="liquidGlass-effect" aria-hidden="true" />
                        <div className="liquidGlass-shine" aria-hidden="true" />
                        <div className="mobile-nav-item cursor-pointer" aria-label="Lock" role="button" tabIndex={0} onClick={() => navigate('/home/blocked-members')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/home/blocked-members'); } }}>
                            <i className="fa-solid fa-lock"></i>
                        </div>
                        <div className="mobile-nav-item cursor-pointer" aria-label="Filter" role="button" tabIndex={0} onClick={() => navigate('/home/member-filter')} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); navigate('/home/member-filter'); } }}>
                            <i className="fa-solid fa-sliders"></i>
                            {filterStatus === 1 ? <span className="filter-active-icon"><i className="fa-solid fa-check"></i></span> : null}
                        </div>
                    </nav>
                    <svg style={{ display: 'none' }} aria-hidden="true">
                        <filter id="glass-distortion-my-connection" x="0%" y="0%" width="100%" height="100%" filterUnits="objectBoundingBox">
                            <feTurbulence type="fractalNoise" baseFrequency="0.01 0.01" numOctaves="1" seed="5" result="turbulence" />
                            <feComponentTransfer in="turbulence" result="mapped">
                                <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
                                <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
                                <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
                            </feComponentTransfer>
                            <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />
                            <feSpecularLighting in="softMap" surfaceScale="5" specularConstant="1" specularExponent="100" lightingColor="white" result="specLight">
                                <fePointLight x="-200" y="-200" z="300" />
                            </feSpecularLighting>
                            <feComposite in="specLight" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="litImage" />
                            <feDisplacementMap in="SourceGraphic" in2="softMap" scale="150" xChannelSelector="R" yChannelSelector="G" />
                        </filter>
                    </svg>
                </>
            )}
        </div>
    )
}